import { useQuery } from '@apollo/client/react';
import { type ReactNode, useMemo } from 'react';
import { Navigate, type Params, useLocation, useParams } from 'react-router';
import { useCurrentUser, useHasPermissions } from 'stores/useUserStore';

import type { SubRoute } from 'enums/routePath';
import type { LDFeatureFlagsKey } from 'hooks/useFeatureFlags';
import type { RequiredPermissions } from 'types/Permissions';

import CoreLayout from 'components/ui/layouts/CoreLayout';
import { RoutePath } from 'enums/routePath';
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import { isFeatureRouteEnabledForUser } from 'utils/featureBundleUtils';

import { userFeatureBundlesQuery } from 'store/api/graph/queries/user';
import type { UserScope } from 'store/client/graphql';

export type RestrictedRouteProps = {
  /**
   * An array of required permissions for accessing the route.
   *
   * @type {RequiredPermissions}
   */
  authorizedPermissions: RequiredPermissions;
  /**
   * An optional array of user scopes that are authorized to access the route.
   * If provided, the user must have at least one of these scopes in addition to the required permissions.
   *
   * @type {UserScope[] | undefined}
   */
  authorizedUserScopes?: UserScope[];
  /**
   * The content to be rendered within the RestrictedRoute.
   *
   * @type {ReactNode}
   */
  children: ReactNode;
  /**
   * An optional feature flag key. If provided, the feature flag must be enabled for the user to access the route.
   *
   * @type {LDFeatureFlagsKey | undefined}
   */
  featureFlag?: LDFeatureFlagsKey;
  /**
   * Determines whether to hide the `CoreLayout` component.
   * If `true`, only the `component` will be rendered.
   * If `false` (default), the `component` will be rendered within the `CoreLayout`.
   *
   * @type {boolean | undefined}
   * @default false
   */
  hideCoreLayout?: boolean;
  /**
   * The path to redirect to if the user is not authorized.
   * Defaults to `RoutePath.HOME`.
   *
   * @type {RoutePath | undefined}
   * @default RoutePath.HOME
   */
  redirectPath?: ((parameters: Readonly<Params<string>>) => string) | `${RoutePath}/${SubRoute}` | RoutePath;
};

const RestrictedRoute = ({
  authorizedPermissions,
  authorizedUserScopes,
  children,
  featureFlag,
  hideCoreLayout = false,
  redirectPath = RoutePath.HOME,
}: RestrictedRouteProps) => {
  const { id, scope: userScope } = useCurrentUser();
  const hasAuthorizedPermissions = useHasPermissions(authorizedPermissions);
  const { flags, ready: featureFlagsReady } = useFeatureFlags();
  const { pathname } = useLocation();
  const parameters = useParams();
  const resolvedRedirectPath = typeof redirectPath === 'function' ? redirectPath(parameters) : redirectPath;

  const { data: userFeatureBundlesData } = useQuery(userFeatureBundlesQuery);

  const featureFlagEnabled = featureFlag ? flags[featureFlag] : true;

  /*
   * Until the user loads (`id` empty) allow a degraded view. Once loaded, require the authorized
   * permissions, plus one of the authorized user scopes when the route restricts scopes.
   * `hasAuthorizedPermissions` is already reactive (useHasPermissions), so no useMemo is needed —
   * the remaining checks are cheap boolean/array ops that recompute trivially each render.
   */
  const hasAuthorizedScope = !authorizedUserScopes?.length || authorizedUserScopes.includes(userScope);
  const isUserAllowedAccess = !id || (hasAuthorizedPermissions && hasAuthorizedScope);

  const isUserAllowedForCurrentRoute = useMemo(
    () => isFeatureRouteEnabledForUser(pathname, userFeatureBundlesData?.user?.rooftops),
    [pathname, userFeatureBundlesData?.user?.rooftops]
  );

  /*
   * LaunchDarkly resolves flags asynchronously, so `flags` is empty on first render. Evaluating the
   * gate then would redirect a user who does have the flag, so hold the route until flags resolve.
   */
  if (featureFlag && !featureFlagsReady) {
    return null;
  }

  if (isUserAllowedForCurrentRoute && isUserAllowedAccess && featureFlagEnabled) {
    return hideCoreLayout ? children : <CoreLayout>{children}</CoreLayout>;
  }

  return <Navigate replace to={resolvedRedirectPath} />;
};

export default RestrictedRoute;
